import { useParams } from "react-router-dom";
import { OverviewDetails } from "./_components/OverviewDetails";
import { useQuery } from "@apollo/client";
import { PERSON_OVERVIEW_QUERY } from "./queries";
import { Person } from "@/types";
import { MetricGridSkeleton, TableSkeleton } from "@/components/Skeleton";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useRecentlyVisited } from "@/hooks/useRecentlyVisited";
import { useCanViewInteractionsBool, useHasCapabilityBool, DOMAIN, PERMISSION } from "@/hooks/useCapabilities";
import { useEffect, useMemo } from "react";
import { renderName } from "@/lib/personHelpers";
import { formatPersonSecondaryLine } from "@/lib/personSecondaryLine";
import { useNatlCommitteeManagementEnabled } from "../../membership-services/committees/useNatlCommitteeManagementEnabled";

export default function PersonOverview() {
  const { pgaId } = useParams();
  const canViewInteractions = useCanViewInteractionsBool();
  const canManageApplications = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.manageApplications
  });
  const useCommitteeHistory = useNatlCommitteeManagementEnabled();
  const showPersonHistoryPreview = canViewInteractions;
  const { addRecentlyVisitedItem } = useRecentlyVisited();

  const { data, error } = useQuery(PERSON_OVERVIEW_QUERY, {
    variables: {
      pgaId: pgaId!,
      includeProgramMetrics: true,
      includeMembershipApplications: canManageApplications,
      useCommitteeHistory
    }
  });

  const person = useMemo<Person | undefined>(() => {
    if (!data?.person) return undefined;
    const { committeeHistory, legacyCommitteeHistory, ...rest } = data.person;
    return {
      ...rest,
      committeeHistory: useCommitteeHistory ? committeeHistory : legacyCommitteeHistory
    };
  }, [data?.person, useCommitteeHistory]);

  useEffect(() => {
    if (person) {
      addRecentlyVisitedItem({
        url: window.location.pathname,
        text: renderName(
          person.displayName,
          person.informalName,
          person.preferredLastName,
          person.suffixName,
          person.middleName,
          person.firstName
        ),
        subtext: formatPersonSecondaryLine(person) || "",
        image: person.profilePhoto || "",
        type: "people"
      });
    }
  }, [person, addRecentlyVisitedItem]);

  if (error) {
    return <ErrorBoundary variant="inline" title="Failed to Load Overview" message={error.message} className="mt-12" />;
  }

  if (!person) {
    return (
      <div data-testid="overview-skeleton">
        <MetricGridSkeleton cards={4} layout="expanded" />
        {showPersonHistoryPreview && <TableSkeleton columns={5} rows={4} />}
      </div>
    );
  }

  return <OverviewDetails person={person} />;
}
